import { DateTime, Duration, Interval } from 'luxon';
import { entityStore, getBinLevelMeasurementOverrides, Measurements, sirenActions } from '~/utility/BinSentry-ui-utility';
import '../../../common/bn-date-picker.js';
import { nothing, styleMap, unsafeCSS } from '~/lib/bn-lit-element';
import { css, html, LitEntity, renderIf } from '../../../utility/lit-entity';
import { StoreMixin } from '../../../utility/store-mixin';
import { sharedStyles } from '../../../styles/shared-styles';
import { LevelColours } from '../../../utility/level-colours-mixin';
import { levelChartStyles } from './level-chart-styles';
import { SmoothLevelHistoryChart } from './smooth-level-history-chart';
import _ from 'lodash';
import SirenParse from 'siren-parser';

class BinLevelSmoothHistoryGraph extends StoreMixin(LitEntity) {
  constructor() {
    super();
    this.points = [];
    this.start = DateTime.now().startOf('day').toUTC().minus({ weeks: 3 }).toISO();
    this.end = DateTime.now().plus({ weeks: 1 }).toUTC().toISO();
    this.maxFutureViewDate = DateTime.now().plus({ weeks: 2 }).toUTC().toISO();
    this.history = true;
    this.measurement = null;
    this.showRaw = null;
    this.statsLoaded = false;
    this.levelsLoaded = false;
    this.axesReady = false;
    this.dataReady = false;
    this.zoomY = false;
    this.levelListenerMap = {};
    this.levelEntitiesList = [];
    this.minDate = null;
    this.manualClassificationsEntityList = [];
    this.inventoryPeriods = [];
    this.showInventoryPeriods = false;

    this._onResize = this._onResize.bind(this);
    this._onKeyPress = this._onKeyPress.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('keydown', this._onKeyPress);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onKeyPress);
    this.levelEntitiesList = [];
    this._updateListeners();
  }

  static get properties() {
    return {
      start: { type: String },
      end: { type: String },
      hideAxes: { type: Boolean, attribute: 'hide-axes' },
      hideToolbar: { type: Boolean, attribute: 'hide-toolbar' },
      hideOptions: { type: Boolean, attribute: 'hide-options' },
      hideLegend: { type: Boolean, attribute: 'hide-legend' },
      hideZoom: { type: Boolean, attribute: 'hide-zoom' },
      timeScale: { type: String, attribute: 'time-scale' },
      heightOverride: { type: String },
      history: { type: Boolean, reflect: true },
      forecast: { type: Boolean, reflect: true },
      points: { type: Array, attribute: false },
      role: { type: String, attribute: false },
      totalVolume: { type: Number, attribute: false },
      levelsHref: { type: String, attribute: false },
      statsHref: { type: String, attribute: false },
      propertiesHref: { type: String, attribute: false },
      minDate: { type: String, attribute: false },
      inventoryPeriodsHref: { type: String, attribute: false },
      // we can't just check for presence of the entities because stats could give back a 204 which leads to
      // an undefined entity.  So instead we track whether each is loaded
      levelsLoaded: { type: Boolean, attribute: false },
      statsLoaded: { type: Boolean, attribute: false },
      propertiesLoaded: { type: Boolean, attribute: false },
      inventoryPeriodsLoaded: { type: Boolean, attribute: false },
      noData: { type: Boolean, attribute: false },
      showRaw: { type: Boolean, attribute: 'show-raw' },
      axesReady: { type: Boolean, attribute: false },
      dataReady: { type: Boolean, attribute: false },
      measurement: { type: Object, attribute: false },
      contentLabel: { type: String, attribute: 'content-label', default: 'feed' },
      highlightSelection: { type: Boolean, attribute: 'highlight-selection' },
      highlightManualFeedMask: { type: Boolean, attribute: 'highlight-manual-feed-mask' },
      focus: { type: String },
      zoomY: { type: Boolean, attribute: 'zoom-y' },
      rangeSelectionStartDate: { type: String, attribute: 'range-selection-start-date' },
      rangeSelectionEndDate: { type: String, attribute: 'range-selection-end-date' },
      inventoryPeriods: { type: Array, attribute: false },
      showInventoryPeriods: { type: Boolean, attribute: 'show-inventory-periods' },
    };
  }

  static get styles() {
    return [sharedStyles, levelChartStyles, css`
      .focusLine,
      .forecast-region {
        pointer-events: none;
      }

      .options {
        display: flex;
        justify-content: flex-end;
        margin: 0 40px;
      }

      .toolbar {
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        margin-left: 60px;
        margin-right: 40px;
        margin-top: 1rem;
      }
      .toolbar .date-range-picker {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .date-range-controls {
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 170px;

      }
      .toolbar .date-range {
        white-space: nowrap;
        display: flex;
        align-items: center;
        padding: 0 8px;
      }

      .vertical-separator {
        width: 1px;
        height: 24px;
        background-color: var(--gray-30);
        align-self: center;
      }

      .date-range-end {
        padding: 0 8px;
        margin: 0 4px;
        color: var(--gray-70);
        display: inline-flex;
        align-items: center;
      }

      .date-picker-input {
        margin: 0 4px;
      }

      .date-range-separator {
        margin: 0;
        color: var(--gray-70);
        display: inline-flex;
        align-items: center;
      }

      .date-today-button {
        margin: 8px;
      }

      .legend {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
      }
      .legend > div {
        margin-right: 1rem;
        display: flex;
        align-items: center;
      }
      .legend > div:last-child {
        margin-right: 0;
      }
      .legend .label {
        flex-grow: 1;
        font-size: .9em;
      }
      .legend .preview {
        border-radius: 3px;
        height: .8rem;
        min-width: .8rem;
        float: left;
        margin: 2px 6px 2px 0;
        display: flex;
      }
      .legend .history .preview {
        display: flex;
      }
      .legend .history .preview div {
        height: 100%;
        width: 10px;
        display: inline-block;
      }
      .legend .history .preview div:first-of-type {
        border-radius: 3px 0 0 3px;
      }
      .legend .history .preview div:last-of-type {
        border-radius: 0 3px 3px 0;
      }

      .legend .history .preview .high {
        background: ${unsafeCSS(LevelColours.getThresholdByName('high').darkColour)};
      }
      .legend .history .preview .medium {
        background: ${unsafeCSS(LevelColours.getThresholdByName('medium').darkColour)};
      }
      .legend .history .preview .low {
        background: ${unsafeCSS(LevelColours.getThresholdByName('low').darkColour)};
      }
      .legend .forecast .preview {
        background: var(--blue-60);
      }
      .legend .inventory-periods .preview-periods {
        display: flex;
        height: 0.8rem;
        width: 2rem;
        border-radius: 3px;
        overflow: hidden;
        margin: 2px 6px 2px 0;
      }
      .legend .inventory-periods .period {
        flex: 1;
        height: 100%;
      }
      .legend .inventory-periods .period.consumption {
        background: rgba(76, 175, 80, 0.6);
      }
      .legend .inventory-periods .period.stable {
        background: rgba(33, 150, 243, 0.6);
      }
      .legend .inventory-periods .period.uncertain {
        background: rgba(158, 158, 158, 0.6);
      }
      .legend .inventory-periods .period.refill {
        background: rgba(255, 193, 7, 0.6);
      }
      .raw-toggle input {
        vertical-align: middle;
      }
      .raw-toggle span {
        vertical-align: middle;
      }

      @media(max-width: 600px) {
        .options {
          display: none;
        }
      }
      @media(max-width: 960px) {
        .toolbar {
          flex-direction: column-reverse;
          justify-content: stretch;
          margin-left: 0;
          margin-right: 0;
          margin-top: 0;
        }
      }
      :host([small]) .toolbar {
        margin: 0;
      }
    `];
  }

  render() {
    let showRaw = this.showRaw;

    if (this.role === 'ingredient' && this.inventoryPeriods.length === 0) {
      showRaw = true;
    }

    const graphClasses = [
      'chart',
      !this.dataReady && 'loading',
      this.noData && 'no-data',
      showRaw && 'show-raw',
    ].filter(Boolean);

    return html`
      <custom-date-range-entity
        .href="${this.levelsHref}"
        .start="${this.start}"
        .end="${this.end}"
        @rangeupdate="${this._onUpdateLevels}"
      ></custom-date-range-entity>

      <lit-entity .href="${this.statsHref}" @update="${this._onUpdateStats}"></lit-entity>
      <lit-entity .href="${this.propertiesHref}" @update="${this._onUpdateProperties}"></lit-entity>
      <lit-entity .href="${this.inventoryPeriodsHref}" @update="${this._onUpdateInventoryPeriods}"></lit-entity>

      ${renderIf(this.role === 'ingredient' && !this.hideZoom, () => html`
        <bn-button variant="text" @click=${() => this.zoomY = !this.zoomY} style="position: relative; z-index: 1; --bn-button-width: 8em;">
          <bn-icon name="far fa-search${this.zoomY ? '-minus' : '-plus'}"></bn-icon>
          ${this.zoomY ? 'Zoom Out' : 'Zoom In'}
        </bn-button>
      `)}

      ${renderIf(!this.hideOptions, () => html`
        <div class="options">
          <label class="raw-toggle">
            <input type="checkbox" ?checked=${this.showRaw} @input="${(value) => this._onToggleRaw(value)}" />
            <span>Raw readings</span>
          </label>
        </div>
      `)}

      <div
        id="graph-container"
        class=${graphClasses.join(' ')}
        style=${styleMap({ ...this.heightOverride ? { height: this.heightOverride } : {} })}
      ></div>

      ${renderIf(!this.hideToolbar, () => html`
        <div class="toolbar">
          </div>
          ${renderIf(this.highlightSelection, () => html`
            <div class="level-picker">
              <bn-button variant="text" @click="${this._prevLevel}">&lt; Previous level</bn-button>
              <bn-button variant="text" @click="${this._nextLevel}">Next level &gt;</bn-button>
            </div>
          `)}
          ${this._renderLegend()}
        </div>
      `)}
    `;
  }

  _renderLegend() {
    if (this.hideLegend) {
      return nothing;
    }

    const items = [
      html`
        <div class="history">
      `,
      (this.forecast && this.statsEntity) && html`
        <div class="forecast">
          <div class="preview"></div>
          <span class="label">Predicted ${this.contentLabel} level</span>
        </div>
      `,
    ];
    return html`<div class='legend'>${items}</div>`;
  }

  updated(changedProperties) {
    if (changedProperties.get('href')) {
      this.points = [];
      this.levelsHref = null;
      this.statsHref = null;
      this.propertiesHref = null;
      this.levelsEntity = null;
      this.statsEntity = null;
      this.propertiesEntity = null;
      this.inventoryPeriodsHref = null;
      this.inventoryPeriodsEntity = null;
      this.inventoryPeriods = [];
    }

    if (changedProperties.has('levelsHref')) {
      this.levelsLoaded = false;
    }

    if (changedProperties.has('statsHref')) {
      this.statsLoaded = false;
    }

    if (changedProperties.has('propertiesHref')) {
      this.propertiesLoaded = false;
    }

    if (changedProperties.has('inventoryPeriodsHref')) {
      this.inventoryPeriodsLoaded = false;
    }

    this.axesReady = !!((this.totalVolume || this.points.length) && this.start && this.end && this.measurement);
    this.dataReady = !!(
      (!this.levelsHref || this.levelsLoaded)
      && (!this.inventoryPeriodsHref || this.inventoryPeriodsLoaded)
      && (this.role === 'ingredient' ||
       (!this.statsHref || this.statsLoaded)
       && this.propertiesLoaded
      )
      && this.measurement
    );

    // only once both sub entities have had a chance to load do we decide if there is no data
    this.noData = this.dataReady && this.points.length === 0;

    if (changedProperties.has('measurement')) {
      this._updateGraph();
    }

    if (changedProperties.has('start') || changedProperties.has('end') || changedProperties.has('inventoryPeriodsEntity')) {
      this._processInventoryPeriods();
    }

    const axesChange = changedProperties.has('axesReady') && this.axesReady;
    const dataChange = (changedProperties.has('dataReady') || changedProperties.has('points')) && this.dataReady;
    const rangeSelectionChange = changedProperties.has('rangeSelectionStartDate') || changedProperties.has('rangeSelectionEndDate');
    const inventoryPeriodsChange = changedProperties.has('inventoryPeriods');
    if (axesChange || dataChange || changedProperties.has('zoomY') || rangeSelectionChange || inventoryPeriodsChange) {
      this._draw();
    }

    if (changedProperties.has('hideToolbar')) {
      this._onResize();
    }
  }

  get element() {
    return this.shadowRoot.querySelector('#graph-container');
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this.levelsHref = this.getHref('https://api.binsentry.com/rel/v2/bin-levels');
    this.statsHref = this.getHref('https://api.binsentry.com/rel/bin-stat-latest');
    this.propertiesHref = this.getHref('https://api.binsentry.com/rel/bin-level-bin-properties');
    this.inventoryPeriodsHref = this.showInventoryPeriods && this.getHref('https://api.binsentry.com/rel/inventory-periods');
    this.totalVolume = this.getProperty('volumes.fullTotal');
    this.bulkDensity = this.getProperty('bulkDensity');
    this.measurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
    this.role = this.getProperty('role');

    this.minDate = null;
    const oldestLevelAction = this.entity?.getAction('get-oldest-bin-level');
    if (oldestLevelAction) {
      const { entity: oldestLevelEntity } = await sirenActions.submitAction(oldestLevelAction);
      this.minDate = oldestLevelEntity?.getProperty('publishedAt');
    }

    if (!this.minDate) {
      this.minDate = this.getProperty('createdAt');
    }
  }

  _stateChanged({ userFeatures }) {
    // don't change if this store update is coming after the user has explicitly chosen to show/not show raw
    this.showRaw = this.showRaw ?? userFeatures?.rawBinLevelsByDefault;
  }

  _onResize() {
    this.chart && this.chart.resize();
  }

  _onKeyPress(e) {
    const input = e.composedPath().filter((n) => n.tagName === 'INPUT')[0];
    if (input) {
      return;
    }

    if (e.key === 'ArrowLeft') {
      this._prevLevel();
    } else if (e.key === 'ArrowRight') {
      this._nextLevel();
    }
  }

  async _onUpdateLevels(e) {
    this.levelsEntity = e.detail.entity;
    this.levelEntitiesList = this.levelsEntity?.entities || [];
    this.manualClassificationsEntityList = e.detail.manualEntity?.entities || [];

    // Fetch latest bin level before date range for rendering anchor
    const action = this.levelsEntity?.getAction('get-latest-bin-level-before-date');
    this.latestBeforeDateEntity = null;

    if (action) {
      try {
        const beforeDate = DateTime.fromISO(this.start).minus({ seconds: 1 }).toISO();
        const { entity: latestBeforeEntity } = await sirenActions.submitAction(action, { beforeDate });
        if (latestBeforeEntity) {
          // Store separately for rendering anchor instead of adding to regular levels
          this.latestBeforeDateEntity = latestBeforeEntity;
        }
      } catch (err) {
        // A 404 is expected if no levels exist before the start date, which we can safely ignore.
        if (err.status !== 404) {
          console.error('Failed to fetch latest level before start date', err);
        }
      }
    }

    this.levelsLoaded = true;
    this._updateListeners();
    this._updateGraph();
  }

  async _onUpdateStats(e) {
    this.statsEntity = e.detail.entity;
    this.statsLoaded = true;
    this._updateGraph();
  }

  async _onUpdateProperties(e) {
    this.propertiesEntity = e.detail.entity;
    this.propertiesLoaded = true;
    this._updateGraph();
  }

  async _onUpdateInventoryPeriods(e) {
    this.inventoryPeriodsEntity = e.detail.entity;
    this.inventoryPeriodsLoaded = true;
    this._processInventoryPeriods();
  }

  _processInventoryPeriods() {
    if (!this.inventoryPeriodsEntity?.entities) {
      this.inventoryPeriods = [];
      return;
    }

    const { bulkDensity, totalVolume } = this;
    const convert = (volume) => this.measurement.convert({ volume, bulkDensity, totalVolume });

    this.inventoryPeriods = this.inventoryPeriodsEntity.entities.map(period => ({
      startDate: DateTime.fromISO(period.properties.startDate),
      endDate: DateTime.fromISO(period.properties.endDate),
      trend: period.properties.trend, // consumption, stable, uncertain, refill
      startVolume: convert(period.properties.startVolume),
      endVolume: convert(period.properties.endVolume),
      consumptionRate: convert(period.properties.consumptionRate),
      consumptionAmount: Math.max(0, convert(period.properties.startVolume - period.properties.endVolume)),
    })).filter(period =>
      // Only include periods that overlap with current view
      period.endDate > DateTime.fromISO(this.start) && period.startDate < DateTime.fromISO(this.end),
    );
  }

  _prevLevel() {
    this.chart?.selectPrevious();
  }

  _nextLevel() {
    this.chart?.selectNext();
  }

  _prev() {
    const oldStart = DateTime.fromISO(this.start);
    const oldEnd = DateTime.fromISO(this.end);
    const hours = Math.round(oldEnd.diff(oldStart, 'hours').hours * 0.75);
    this.start = oldStart.minus({ hours }).toISO();
    this.end = oldEnd.minus({ hours }).toISO();
    this.levelsLoaded = false;
  }

  _next() {
    const oldStart = DateTime.fromISO(this.start);
    const oldEnd = DateTime.fromISO(this.end);
    const hours = Math.round(oldEnd.diff(oldStart, 'hours').hours * 0.75);
    this.start = oldStart.plus({ hours }).toISO();
    this.end = oldEnd.plus({ hours }).toISO();
    this.levelsLoaded = false;
  }

  _today() {
    if (this.role === 'ingredient') {
      const endTime = DateTime.now().startOf('hour').plus({ hours: 2 }).toUTC();
      this.start = endTime.minus({ days: 1 }).toISO();
      this.end = endTime.toISO();
    } else {
      this.start = DateTime.now().startOf('day').toUTC().minus({ weeks: 3 }).toISO();
      this.end = DateTime.now().plus({ weeks: 1 }).toUTC().toISO();
    }
    this.levelsLoaded = false;
  }

  _getHistory() {
    const levels = this.levelEntitiesList;
    const smoothingDisabled = this.propertiesEntity?.properties.smoothingModel === 'none';
    if (!this.history || !levels) {
      return [];
    }
    const { bulkDensity, totalVolume } = this;
    const convert = (volume) => this.measurement.convert({ volume, bulkDensity, totalVolume });
    const points = levels.map((level) => {
      const { publishedAt, volume } = level.properties;
      const id = level.getLinkByRel('self').href;
      const currentEstimate = smoothingDisabled ? undefined : level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-current');
      const initialEstimate = level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-initial');
      const feedMask = level.getSubEntity('https://api.binsentry.com/rel/manual-feed-mask-data');

      return {
        ...level.properties,
        id,
        value: convert(volume),
        estimatedValue: currentEstimate && convert(currentEstimate.properties.estimatedVolume),
        initialEstimatedValue: initialEstimate && convert(initialEstimate.properties.estimatedVolume),
        date: DateTime.fromISO(publishedAt),
        feedMask,
      };
    });
    return _.sortBy(points, 'date');
  }

  _getForecast() {
    if (!this.forecast || !this.statsEntity) {
      return [];
    }
    const points = [];
    const { forecast } = this.statsEntity.properties.volume;
    if (!forecast) {
      return [];
    }
    const { bulkDensity, totalVolume } = this;
    forecast.forEach((f) => {
      const date = DateTime.fromISO(f.publishedAt);
      const start = DateTime.fromISO(this.start);
      const end = DateTime.fromISO(this.end);
      if (!Interval.fromDateTimes(start, end).contains(date)) {
        return;
      }
      const volume = f.low;
      const value = this.measurement.convert({ volume, bulkDensity, totalVolume });
      const weight = volume * bulkDensity;
      points.push({ value, date, volume, weight, forecast: true });
    });
    return points;
  }

  _getLatestBeforeDateLevel() {
    const level = this.latestBeforeDateEntity;
    if (!level || !level.properties) {
      return null;
    }

    const smoothingDisabled = this.propertiesEntity?.properties.smoothingModel === 'none';
    const { publishedAt, volume } = level.properties;
    const { bulkDensity, totalVolume } = this;
    const convert = (volume) => this.measurement.convert({ volume, bulkDensity, totalVolume });

    const currentEstimate = smoothingDisabled ? undefined : level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-current');
    const initialEstimate = level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-initial');

    return {
      value: convert(volume),
      estimatedValue: currentEstimate && convert(currentEstimate.properties.estimatedVolume),
      initialEstimatedValue: initialEstimate && convert(initialEstimate.properties.estimatedVolume),
      date: DateTime.fromISO(publishedAt),
    };
  }

  _updateListeners() {
    const levelIds = this.levelEntitiesList.map(level => level.getLink('self').href);
    const existingListenerKeys = Object.keys(this.levelListenerMap || {});
    const newListenerKeys = _.difference(levelIds, existingListenerKeys);
    const removeListenerKeys = _.difference(existingListenerKeys, levelIds);

    const listenerFunction = ({ entity: rawEntity }) => {
      const entity = SirenParse(rawEntity);
      const key = entity.getLink('self').href;
      const index = _.findIndex(this.levelEntitiesList, e => e.getLink('self').href === key);
      if (index >= 0) {
        this.levelEntitiesList.splice(index, 1, entity);
        this._updateGraph();
      }
    };
    newListenerKeys.forEach(key => {
      this.levelListenerMap[key] = entityStore.addEntityListener(key, listenerFunction);
    });
    removeListenerKeys.forEach(key => {
      entityStore.removeEntityListener(key, this.levelListenerMap[key]);
      delete this.levelListenerMap[key];
    });
  }

  _updateGraph() {
    if (!this.measurement) {
      this.points = [];
      return;
    }
    const history = this._getHistory();
    const lastHistory = history.length > 0 && history[history.length - 1];
    let forecast = this._getForecast();
    if (lastHistory && forecast.length) {
      // only include forecasts that are after the last history (+1 hour)
      forecast = forecast.filter((p) => p.date > lastHistory.date.plus({ hours: 1 }));
    }
    this.points = [...history, ...forecast];
  }

  _onToggleRaw() {
    this.showRaw = !this.showRaw;
  }

  _draw() {
    const { measurement } = this;
    const maxValue = Math.max(...this.points.map(p => p.value)) || 0;
    const upperLimit = measurement.metric === 'ratio' ? 100 : measurement.convert({ volume: this.totalVolume, bulkDensity: this.bulkDensity });
    const yMax = Math.max(maxValue, upperLimit) * 1.05;
    const yRange = {
      start: 0,
      end: yMax,
    };

    if (this.zoomY) {
      const values = this.points.filter(p => p.label !== 'OUTLIER').map(p => p.value);
      const high = Math.max(...values, 0);
      const low = Math.min(...values, high);
      // if the range is too small, the 2% margin of error will look like movement
      // that isn't really there, so we ensure the range is at least 5% of the total volume
      const range = Math.max(high - low, this.totalVolume * 0.05);
      const padding = range * 0.5;
      yRange.start = Math.max(0, low - padding);
      yRange.end = Math.min(high + padding, upperLimit);
    }
    const focusDate = this.focus ? DateTime.fromISO(this.focus) : null;
    const selectedDate = this.chart?.selectedPoint?.date || focusDate;
    const selectionDateRange = (this.rangeSelectionStartDate && this.rangeSelectionEndDate) ? {
      startPublishedDate: this.rangeSelectionStartDate,
      endPublishedDate: this.rangeSelectionEndDate,
    } : null;
    this.chart = new SmoothLevelHistoryChart({
      onSelect: (point) => this.dispatchEvent(new CustomEvent('bn:select-data-point', { detail: { point } })),
      selectionDateRange,
      element: this.element,
      hideAxes: this.hideAxes,
      points: this.dataReady ? this.points : [],
      unit: measurement.unit,
      xRange: {
        start: this.start,
        end: this.end,
      },
      yRange,

      // Pass the latest-before-date level for rendering anchor
      latestBeforeDateLevel: this.dataReady ? this._getLatestBeforeDateLevel() : null,
      manualClassificationsEntityList: this.dataReady ? this.manualClassificationsEntityList : [],

      ...this.role === 'ingredient' ? {
        timeScale: this.timeScale || 'hours',
        hideLine: true,
        hideRefills: true,
        // Inventory periods overlay
        inventoryPeriods: this.dataReady ? this.inventoryPeriods : [],
        showInventoryPeriods: this.showInventoryPeriods,
      } : {
        timeScale: this.timeScale || 'days',
      },

      highlightWidth: 10,
      highlightSelection: this.highlightSelection,
      highlightManualFeedMask: this.highlightManualFeedMask,

      // while we can grow the y-axis to scale to unexpectedly out-of-range values, the colours should be computed
      // solely based on the bin capacity to make it consistent with the bin profile (unless upperLimit is 0 to avoid NaN)
      fullValue: upperLimit || yMax,
    });
    // if focus is present, and in range, select it
    if (selectedDate) {
      const inRange = Interval.fromDateTimes(DateTime.fromISO(this.start), DateTime.fromISO(this.end)).contains(selectedDate);
      if (inRange) {
        this.chart.selectByDate(selectedDate.toJSDate());
      }
    }
  }

  _onStartDateChange(e) {
    const val = e.detail.value;
    if (val) {
      const startDateTime = DateTime.fromISO(val).toUTC();
      this.start = startDateTime.toISO();

      const duration = this.role === 'ingredient'
        ? Duration.fromObject({ hours: 24 })
        : Duration.fromObject({ weeks: 4 });

      this.end = startDateTime.plus(duration).toISO();

      this.levelsLoaded = false;
      this._fetchLevelsData();
    }
  }

  _fetchLevelsData() {
    const dateRangeEntity = this.shadowRoot.querySelector('custom-date-range-entity');
    if (dateRangeEntity) {
      dateRangeEntity.fetchData();
    }
  }
}

window.customElements.define('bin-level-smooth-history-graph', BinLevelSmoothHistoryGraph);
